import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Building2,
  Users,
  ScrollText,
  FileText,
  Flag,
  type LucideIcon,
} from "lucide-react";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { cn } from "@/shared/lib/utils";

interface PhaseCard {
  readonly icon: LucideIcon;
  readonly titleKey: string;
  readonly descriptionKey: string;
  readonly phase: string;
  /**
   * When set, the card is a navigation link to the feature page.
   * When null, the feature is still blocked (backend not ready) and the
   * card renders as an informational tile with a "coming soon" badge.
   */
  readonly href: string | null;
}

// Phase 1 slices from docs/backoffice-roadmap.md §5. Shipped features
// become nav shortcuts to their page; blocked ones stay informational
// with a "coming soon" badge. Remove the null + the badge when the
// backend ask lands for each of them.
const phaseCards: readonly PhaseCard[] = [
  {
    icon: Building2,
    titleKey: "dashboard.phase1.venues.title",
    descriptionKey: "dashboard.phase1.venues.description",
    phase: "BO-1.1",
    href: "/venues",
  },
  {
    icon: Users,
    titleKey: "dashboard.phase1.users.title",
    descriptionKey: "dashboard.phase1.users.description",
    phase: "BO-1.2",
    href: "/users",
  },
  {
    icon: ScrollText,
    titleKey: "dashboard.phase1.auditLog.title",
    descriptionKey: "dashboard.phase1.auditLog.description",
    phase: "BO-1.3",
    // Waits on API-1.10 (read endpoint + controller).
    href: null,
  },
  {
    icon: FileText,
    titleKey: "dashboard.phase1.tos.title",
    descriptionKey: "dashboard.phase1.tos.description",
    phase: "BO-1.4",
    href: "/tos",
  },
  {
    icon: Flag,
    titleKey: "dashboard.phase1.featureFlags.title",
    descriptionKey: "dashboard.phase1.featureFlags.description",
    phase: "BO-1.5",
    href: "/feature-flags",
  },
];

export function DashboardPage() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const name = user?.displayName ?? user?.email ?? "";

  return (
    <div data-testid="dashboard-screen" className="space-y-10">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("dashboard.eyebrow")}</p>
        <h1 className="text-4xl">{t("dashboard.title")}</h1>
        <p
          data-testid="dashboard-welcome"
          className="font-sans text-base font-normal text-muted-foreground"
        >
          {t("dashboard.welcome", { name })}
        </p>
      </header>

      <section className="space-y-4">
        <p className="brand-eyebrow">{t("dashboard.phase1.eyebrow")}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {phaseCards.map((card) => (
            <PhaseCardTile key={card.phase} card={card} />
          ))}
        </div>
      </section>
    </div>
  );
}

function PhaseCardTile({ card }: { readonly card: PhaseCard }) {
  const { t } = useTranslation();
  const Icon = card.icon;
  const testId = `phase-card-${card.phase.toLowerCase()}`;

  const body = (
    <>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md bg-wander-orange/10 text-wander-orange">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-wander-orange">
              {card.phase}
            </span>
            <CardTitle className="text-lg">{t(card.titleKey)}</CardTitle>
          </div>
        </div>
        {card.href ? (
          <ArrowUpRight
            className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-wander-orange"
            aria-hidden="true"
          />
        ) : (
          <span
            data-testid={`${testId}-badge`}
            className="flex-shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
          >
            {t("dashboard.phase1.comingSoon")}
          </span>
        )}
      </CardHeader>
      <CardContent>
        <p className="font-sans text-sm font-normal text-muted-foreground">
          {t(card.descriptionKey)}
        </p>
      </CardContent>
    </>
  );

  if (card.href) {
    return (
      <Link
        to={card.href}
        data-testid={testId}
        className="group block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-label={t(card.titleKey)}
      >
        <Card
          className={cn(
            "h-full border-border/60 transition-colors",
            "group-hover:border-wander-orange/60 group-hover:shadow-sm",
          )}
        >
          {body}
        </Card>
      </Link>
    );
  }

  return (
    <Card
      data-testid={testId}
      className="h-full cursor-not-allowed border-border/60 opacity-75"
      aria-disabled="true"
    >
      {body}
    </Card>
  );
}
